import axios from 'axios';
import { get, set } from 'lodash-es';
import moment from 'moment';
import qs from 'qs';
import { httpClient as commonHttpClient } from '@shipstation/client-common/api/client';
import { configureLogger } from '@shipstation/client-common/services/logger';

import * as AuthConstants from './authentication/constants';
import LoginService from './authentication/login/login-service';
import LogoutService from './authentication/logout/logout-service';
import * as Cookies from './constants/cookies';
import { gitData } from './constants/git-data';
import { emptyObject } from 'ui-components/default-props/empty';
import { mfaRequiredSubject } from './global/step-up-authentication/account-verification-needed.subject';
import { checkResponseIsMfaNeeded, checkResponseIsEmailVerificationRequired } from './global/step-up-authentication/helpers';
import { Auth0Wrapper } from './services/auth0/Auth0Wrapper';
import { CookieService } from './services/cookie-service';
import { DropInRestrictionsService } from './services/dropin-restrictions-service';
import { LocalStorageService } from './services/local-storage-service';
import { LogService } from './services/log-service';
import { bulkload } from './store/entities/entities.bulkload';
import { ratePatcher } from './store/entities/entities.rate-patcher';

configureLogger(LogService);

const dateTimeList = [
  'holdUntilDateTime',
  'orderDateTime',
  'paidDateTime',
  'shipByDateTime',
  'createdDateTime',
  'modifiedDateTime',
  {
    key: 'value',
    route: '/api/SalesOrder/{salesOrderId}/setHoldUntilDate',
  },
  {
    key: 'value',
    route: '/api/SalesOrder/{salesOrderId}/setPaidDate',
  },
  'shipDateTime',
  'expectedDeliveryDateTime',
  'deliveryDate',
  'shipDate',
  'suggestedShipDate',
  'verificationUtc',
  'labelCreatedDateTime',
  'createDate',
  'orderDate',
  { route: '/api/ordergrid', key: 'orderedAfter' },
  { route: '/api/ordergrid', key: 'orderedBefore' },
  { route: '/api/ordergrid', key: 'paidAfter' },
  { route: '/api/ordergrid', key: 'paidBefore' },
];

/**
 *
 */
function objectToPaths(ob) {
  const validId = /^[a-z_$][a-z0-9_$]*$/i;
  const result = [];

  /**
   *
   */
  function doIt(data, s) {
    if (data && typeof data === 'object') {
      if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
          doIt(data[i], `${s}[${i}]`);
        }
      } else {
        Object.keys(data).forEach((p) => {
          if (validId.test(p)) {
            doIt(data[p], `${s}.${p}`);
          } else {
            doIt(data[p], `${s}["${p}"]`);
          }
        });
      }
    } else {
      result.push(s);
    }
  }

  doIt(ob, '');
  return result;
}

const transformDates = (data) => {
  const paths = objectToPaths(data);

  paths.forEach((path) => {
    const shouldTransformDate = dateTimeList.reduce((isInList, dtCheck) => {
      if (isInList) {
        return isInList;
      }

      return path.endsWith(`.${dtCheck}`);
    }, false);

    if (shouldTransformDate) {
      const transformPath = path.substr(1);
      const date = get(data, transformPath);
      let isoTransformedDate;
      if (date) {
        try {
          const transformedDate = moment(date);
          if (!transformedDate.isValid()) {
            throw new Error(`Date is not valid ${date}`);
          }
          isoTransformedDate = transformedDate.toISOString();
          set(data, transformPath, isoTransformedDate);
        } catch (e) {
          LogService.logInfo('Error caught during date transformation', e);
        }
      }
    }
  });
};

const isUnauthorized = (response) => {
  return response && response.status === 401;
};

const handleUnauthorizedLogout = (response) => {
  const { location } = response.data;
  if (location) {
    CookieService.removeCookie(Cookies.SSHome);
    CookieService.setCookieValueWithExpiration(Cookies.SSHome, location.replaceAll('/', ''), 365 * 2);
    LocalStorageService.clear();
    window.location.href = location;
  } else {
    LogoutService.logout('/logout');
  }
};

const isMaintenance = (response) => {
  return response && response.status === 503;
};

const handleMaintenanceLogout = () => {
  return LogoutService.logout('/logout', AuthConstants.LogoutTypeMaintenance);
};

const customDefaultHeaders = {
  'git-version': gitData,
};

export const addCustomDefaultHeadersToAxiosRequests = (newHeaders = {}) => {
  Object.entries(newHeaders).map(([key, value]) => (customDefaultHeaders[key] = value));
};

let dispatch;
export const setDispatch = (newDispatch) => {
  dispatch = newDispatch;
};

const serializeQueryParams = (queryParams) => {
  if (!queryParams) {
    return;
  }

  transformDates(queryParams);

  return qs.stringify(queryParams, {
    indicies: false,
    arrayFormat: 'repeat',
  });
};

const rethrowRequestError = (error) => {
  throw error;
};

/**
 * Under native auth this attaches no bearer: `ss-ui-node`'s API proxy derives
 * `authorization-token` from the `auth` cookie server-side.
 *
 * @param {object} [options]
 * @param {boolean} [options.isNativeAuth]
 */
const createAuthRequestInterceptor =
  (options = {}) =>
  async (config) => {
    const configWithUpdatedHeaders = {
      ...config,
      headers: config.headers ? { ...config.headers, ...customDefaultHeaders } : customDefaultHeaders,
    };

    if (!Auth0Wrapper.isUsingAuth0Auth() || options.isNativeAuth) {
      // native auth, do nothing...
      return configWithUpdatedHeaders;
    }

    try {
      let accessToken;
      if (Auth0Wrapper.isUsingSsasAuth()) {
        accessToken = Auth0Wrapper.getSsasAccessToken();
      } else {
        accessToken = await Auth0Wrapper.getAccessToken();
      }

      return {
        ...configWithUpdatedHeaders,
        headers: {
          ...configWithUpdatedHeaders.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
    } catch {
      // WARNING: never log here using the http log service!!! (recursion death-spiral warning)
      // expected auth0 exception on e.error === 'login_required'

      // TODO: this is the end goal, but moving to this now will likely present a new unintended logout
      // Future state: await handleUnauthorizedLogout(error.response);
      // Future state: throw error;

      // Temporary state: do not log user out (mimics current behavior)
      // Allow currently loaded app to continue session until _lat* token expires and forces a full logout
      return configWithUpdatedHeaders;
    }
  };

const configuredInstances = new WeakSet();

/**
 * @param {import('axios').AxiosInstance} instance
 * @returns {boolean} true when the instance has already been configured
 */
const isAxiosInstanceConfigured = (instance) => {
  return configuredInstances.has(instance);
};

/**
 * @param {import('axios').AxiosInstance} instance
 * @returns {void}
 */
const registerConfiguredInstance = (instance) => {
  configuredInstances.add(instance);
};

/**
 *
 */
function transformRequestDates(data, headers) {
  if (!data) {
    return;
  }

  // FormData must be passed through untouched so the browser can
  // set Content-Type to multipart/form-data with the correct boundary.
  if (data instanceof FormData) {
    return data;
  }

  transformDates(data);

  const existingContentType = headers['Content-Type'] ?? headers['content-type'];
  if (!existingContentType) {
    // oxlint-disable-next-line no-param-reassign
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }

  const contentType = String(headers['Content-Type'] ?? headers['content-type'] ?? '').toLowerCase();
  if (contentType && !contentType.includes('application/json')) {
    return data;
  }

  return JSON.stringify(data);
}

/**
 * @param {import('axios').AxiosInstance} instance
 * @param {object} [options]
 * @param {boolean} [options.isNativeAuth]
 */
const applyLegacyAxiosConfig = (instance, options = {}) => {
  if (isAxiosInstanceConfigured(instance)) {
    return;
  }

  Object.assign(instance.defaults, {
    baseURL: '/api',
    paramsSerializer: serializeQueryParams,
    transformRequest: [transformRequestDates],
  });

  instance.interceptors.request.use(createAuthRequestInterceptor(options), rethrowRequestError);

  instance.interceptors.response.use(
    (response) => {
      const renewToken = response.headers['renew-token'];
      if (renewToken) {
        LoginService.setAuthToken(renewToken);
      }

      if (dispatch) {
        ratePatcher(get(response, 'config.url'), get(response, 'data', emptyObject), dispatch);
        bulkload(get(response, 'config.url'), get(response, 'data', emptyObject), dispatch);
      }

      return response;
    },
    async (error) => {
      const isMfaRequired = checkResponseIsMfaNeeded(error);
      const isNativeVerificationRequired = checkResponseIsEmailVerificationRequired(error);

      const config = error?.config;

      if ((isMfaRequired || isNativeVerificationRequired) && config && !config.ss_isMfaRetry) {
        // prevent infinite loops
        config.ss_isMfaRetry = true;

        return new Promise((resolve, reject) => {
          mfaRequiredSubject.next({
            config,
            resolve,
            reject,
            url: config.url,
            method: config.method,
            isNative: isNativeVerificationRequired,
          });
        });
      }

      const { response } = error;

      if (isUnauthorized(response)) {
        handleUnauthorizedLogout(response);
      } else if (isMaintenance(response)) {
        await handleMaintenanceLogout();
      } else if (DropInRestrictionsService.isResponseUnauthorizedDropInAction(response)) {
        DropInRestrictionsService.showDropinActionRestrictedMessage(response);
      }

      throw error;
    },
  );

  registerConfiguredInstance(instance);
};

/**
 * client-common owns the canonical axios instance (`httpClient`) and expects the
 * consuming app to add config to it — see `shipstation-lite/src/app-entry.tsx`.
 * ss-ui-client predates that and configures the global axios default instance,
 * which hundreds of modules import directly.
 *
 * Until those modules move onto `httpClient`, both instances get the same config:
 * the global one for legacy app code, `httpClient` so client-common code runs with
 * ss-ui-client's auth, baseURL, and step-up auth response chain. Left unconfigured,
 * `httpClient` requests resolve against the SPA's own origin, where `ss-ui-node`'s
 * catch-all answers a GET with `index.html` and a 200 that callers parse as their payload.
 *
 * `X-Platform-Id` is intentionally omitted: `client-common` defines a platform id – "9" – for lite only.
 *
 * Drop the global instance from this function once app code imports `httpClient`.
 *
 * @param {object} [options]
 * @param {boolean} [options.isNativeAuth]
 */
export const setupAxios = (options = {}) => {
  applyLegacyAxiosConfig(axios, options);
  applyLegacyAxiosConfig(commonHttpClient, options);
};
